import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import MatchCard from "@/components/MatchCard";
import PredictionDialog from "@/components/PredictionDialog";

export default function Fixtures({ competition, readOnly = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const code = competition.code;

  const load = useCallback((round) => {
    setData(null);
    http.get(readOnly ? "/public/matches" : "/matches", { params: { competition: code, ...(round ? { round } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, [code, readOnly]);

  useEffect(() => { load(); }, [load]);

  const [bulk, setBulk] = useState(false);
  const generateAll = async () => {
    setBulk(true);
    try {
      const r = await http.post("/predictions/bulk", null, { params: { competition: code, round: data.round } });
      if (!r.data.queued) { toast.info("Tutte le partite da giocare hanno già un pronostico"); setBulk(false); return; }
      toast.success(`${r.data.queued} pronostici AI in generazione (circa ${r.data.queued * 15} secondi)`);
      const rid = data.round;
      let ticks = 0;
      const t = setInterval(async () => {
        ticks += 1;
        const res = await http.get("/matches", { params: { competition: code, round: rid } });
        setData(res.data);
        const missing = res.data.matches.filter((m) => m.status !== "FINISHED" && !m.prediction).length;
        if (!missing || ticks > r.data.queued * 2) { clearInterval(t); setBulk(false); toast.success("Pronostici della giornata pronti"); }
      }, 15000);
    } catch (e) { toast.error(errMsg(e)); setBulk(false); }
  };

  const onPredicted = (matchId, pred) => {
    setData((d) => ({ ...d, matches: d.matches.map((m) => (m.id === matchId ? { ...m, prediction: pred } : m)) }));
  };

  const rounds = data?.rounds || [];
  const idx = rounds.findIndex((r) => r.id === data?.round);
  const label = rounds[idx]?.label || "–";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="font-mono text-xs text-slate-400 tracking-widest uppercase">{competition.name}</span>
          <h2 data-testid="round-title" className="font-display text-4xl font-extrabold uppercase">{label}</h2>
        </div>
        <div data-testid="matchday-selector" className="flex flex-wrap items-center gap-2">
          <button data-testid="matchday-prev-btn" className="btn btn-ghost" disabled={idx <= 0} onClick={() => load(rounds[idx - 1].id)}><ChevronLeft size={16} /></button>
          <select data-testid="matchday-select" value={data?.round ?? ""} onChange={(e) => load(e.target.value)} className="bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm font-mono max-w-[220px]">
            {rounds.map((r) => (
              <option key={r.id} value={r.id}>{r.label}{data.current_round === r.id ? " (prossima)" : ""}</option>
            ))}
          </select>
          <button data-testid="matchday-next-btn" className="btn btn-ghost" disabled={idx < 0 || idx >= rounds.length - 1} onClick={() => load(rounds[idx + 1].id)}><ChevronRight size={16} /></button>
          <button data-testid="matchday-refresh-btn" className="btn btn-ghost" onClick={() => load(data?.round)}><RefreshCw size={16} /></button>
          {!readOnly && <button data-testid="bulk-generate-btn" disabled={!data || bulk} onClick={generateAll} className={`btn btn-gold ${bulk ? "scan" : ""}`}><Sparkles size={16} /> {bulk ? "Generazione..." : "Genera tutta la giornata"}</button>}
        </div>
      </div>

      {!data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="panel h-40 scan" />)}
        </div>
      ) : data.matches.length === 0 ? (
        <p data-testid="no-matches" className="text-slate-400">Nessuna partita disponibile per questa competizione.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {data.matches.map((m, i) => (
            <MatchCard key={m.id} match={m} code={code} readOnly={readOnly} index={i + 1} onOpen={() => setOpen(m)} onPredicted={onPredicted} />
          ))}
        </div>
      )}

      {open && (
        <PredictionDialog
          match={data.matches.find((m) => m.id === open.id) || open}
          code={code}
          readOnly={readOnly}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}
